import { LESSON_STATUS, SECTION_TYPES } from "../LessonPlannerConstant";
import { formatDate, formatTimeRange, getPhaseMeta, getTotalDuration } from "./lessonPlanUtils";

/* ================= LAYOUT ================= */
const PAGE = { width: 595.28, height: 841.89 }; // A4 in points
const MARGIN = 48;
const CONTENT_WIDTH = PAGE.width - MARGIN * 2;
const FOOTER_HEIGHT = 28;

const SIZE = { title: 18, heading: 12.5, body: 10, small: 8.5 };
const LINE_GAP = 1.45;

/* ================= TEXT HELPERS ================= */

// Standard PDF fonts only cover WinAnsi; map common extras and drop the rest
const CHAR_MAP = { "₀": "0", "₁": "1", "₂": "2", "₃": "3", "₄": "4", "²": "2", "³": "3", "→": "->", "✓": "-", "•": "•" };

const decodeEntities = (text) =>
  text
    .replace(/&nbsp;/g, " ")
    .replace(/&amp;/g, "&")
    .replace(/&lt;/g, "<")
    .replace(/&gt;/g, ">")
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'");

/** Rich-text HTML -> plain lines; list items become "• item". */
const htmlToLines = (html = "") =>
  decodeEntities(
    html
      .replace(/<li[^>]*>/gi, "\n• ")
      .replace(/<\/(p|div|li|ul|ol|h[1-6])>/gi, "\n")
      .replace(/<br\s*\/?>/gi, "\n")
      .replace(/<[^>]+>/g, ""),
  )
    .split("\n")
    .map((line) => line.trim())
    .filter(Boolean);

const toFileName = (title) =>
  `lesson-plan-${(title || "untitled").toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "") || "untitled"}.pdf`;

/* ================= WRITER ================= */

const createWriter = ({ pdfDoc, fonts, rgb }) => {
  const color = {
    text: rgb(0.118, 0.161, 0.231), // #1e293b
    muted: rgb(0.392, 0.455, 0.545), // #64748b
    primary: rgb(0.31, 0.275, 0.898), // #4f46e5
    border: rgb(0.886, 0.91, 0.941), // #e2e8f0
    soft: rgb(0.933, 0.949, 1), // #eef2ff
  };

  let page;
  let y;
  const encodable = new Map();

  const sanitize = (text, font) =>
    [...[...String(text ?? "")].map((char) => CHAR_MAP[char] ?? char).join("")]
      .filter((char) => {
        const key = `${font.name}:${char}`;
        if (!encodable.has(key)) {
          try {
            font.widthOfTextAtSize(char, SIZE.body);
            encodable.set(key, true);
          } catch {
            encodable.set(key, false);
          }
        }
        return encodable.get(key);
      })
      .join("");

  const wrap = (text, font, size, width) => {
    const lines = [];
    sanitize(text, font)
      .split("\n")
      .forEach((paragraph) => {
        let line = "";
        paragraph.split(/\s+/).forEach((word) => {
          const candidate = line ? `${line} ${word}` : word;
          if (font.widthOfTextAtSize(candidate, size) <= width) {
            line = candidate;
          } else {
            if (line) lines.push(line);
            line = word;
          }
        });
        lines.push(line);
      });
    return lines;
  };

  const addPage = () => {
    page = pdfDoc.addPage([PAGE.width, PAGE.height]);
    y = PAGE.height - MARGIN;
  };

  const ensureSpace = (height) => {
    if (y - height < MARGIN + FOOTER_HEIGHT) addPage();
  };

  const space = (height) => {
    y -= height;
  };

  /** Wrapped paragraph; `indent` shifts it right, `prefix` hangs (e.g. "1." or "•"). */
  const paragraph = (text, { font = fonts.regular, size = SIZE.body, tint = color.text, indent = 0, prefix = "" } = {}) => {
    if (!text) return;
    const prefixWidth = prefix ? font.widthOfTextAtSize(`${prefix} `, size) : 0;
    const lineHeight = size * LINE_GAP;
    wrap(text, font, size, CONTENT_WIDTH - indent - prefixWidth).forEach((line, index) => {
      ensureSpace(lineHeight);
      y -= lineHeight;
      if (prefix && index === 0) {
        page.drawText(prefix, { x: MARGIN + indent, y, size, font, color: color.primary });
      }
      page.drawText(line, { x: MARGIN + indent + prefixWidth, y, size, font, color: tint });
    });
  };

  const heading = (text, meta) => {
    ensureSpace(52);
    space(24);
    page.drawRectangle({ x: MARGIN, y: y - 4, width: 3, height: SIZE.heading + 4, color: color.primary });
    page.drawText(sanitize(text, fonts.bold), { x: MARGIN + 10, y, size: SIZE.heading, font: fonts.bold, color: color.text });
    if (meta) {
      const offset = MARGIN + 10 + fonts.bold.widthOfTextAtSize(sanitize(text, fonts.bold), SIZE.heading) + 8;
      page.drawText(sanitize(meta, fonts.regular), { x: offset, y, size: SIZE.small, font: fonts.regular, color: color.muted });
    }
    space(10);
  };

  /** Two-column label/value grid. */
  const infoGrid = (rows) => {
    const visible = rows.filter(([, value]) => value !== "" && value !== null && value !== undefined);
    const columnWidth = CONTENT_WIDTH / 2;
    const rowHeight = 30;

    const drawCell = ([label, value], column) => {
      const x = MARGIN + column * columnWidth;
      page.drawText(sanitize(label, fonts.regular).toUpperCase(), { x, y: y + 14, size: SIZE.small - 1, font: fonts.regular, color: color.muted });
      const [firstLine] = wrap(String(value), fonts.bold, SIZE.body, columnWidth - 12);
      page.drawText(firstLine, { x, y: y + 1, size: SIZE.body, font: fonts.bold, color: color.text });
    };

    const pairs = visible.reduce((acc, cell, index) => {
      if (index % 2 === 0) acc.push([cell]);
      else acc[acc.length - 1].push(cell);
      return acc;
    }, []);

    pairs.forEach((pair) => {
      ensureSpace(rowHeight);
      y -= rowHeight;
      pair.forEach(drawCell);
    });
  };

  /** Shaded box, e.g. Teacher Remarks. */
  const callout = (label, text) => {
    const lines = wrap(text, fonts.regular, SIZE.body, CONTENT_WIDTH - 24);
    const height = 26 + lines.length * SIZE.body * LINE_GAP;
    ensureSpace(height + 12);
    space(12);
    page.drawRectangle({ x: MARGIN, y: y - height, width: CONTENT_WIDTH, height, color: color.soft, borderColor: color.border, borderWidth: 0.6 });
    page.drawText(sanitize(label, fonts.bold), { x: MARGIN + 12, y: y - 16, size: SIZE.body, font: fonts.bold, color: color.primary });
    lines.forEach((line, index) => {
      page.drawText(line, { x: MARGIN + 12, y: y - 30 - index * SIZE.body * LINE_GAP, size: SIZE.body, font: fonts.regular, color: color.text });
    });
    space(height);
  };

  const divider = () => {
    ensureSpace(14);
    space(8);
    page.drawLine({ start: { x: MARGIN, y }, end: { x: PAGE.width - MARGIN, y }, thickness: 0.6, color: color.border });
    space(6);
  };

  /** Page x of n + generated date on every page. */
  const footers = (generatedOn) => {
    const pages = pdfDoc.getPages();
    pages.forEach((p, index) => {
      const label = `Page ${index + 1} of ${pages.length}`;
      p.drawLine({ start: { x: MARGIN, y: MARGIN }, end: { x: PAGE.width - MARGIN, y: MARGIN }, thickness: 0.5, color: color.border });
      p.drawText(`Generated on ${generatedOn}`, { x: MARGIN, y: MARGIN - 14, size: SIZE.small, font: fonts.regular, color: color.muted });
      p.drawText(label, {
        x: PAGE.width - MARGIN - fonts.regular.widthOfTextAtSize(label, SIZE.small),
        y: MARGIN - 14,
        size: SIZE.small,
        font: fonts.regular,
        color: color.muted,
      });
    });
  };

  addPage();
  return { paragraph, heading, infoGrid, callout, divider, footers, space, sanitize, color, fonts };
};

/* ================= DOCUMENT ================= */

/** Builds the lesson plan PDF and returns its bytes. */
export const buildLessonPlanPdf = async (lesson) => {
  // Loaded on demand so pdf-lib is not part of the main bundle
  const { PDFDocument, StandardFonts, rgb } = await import("pdf-lib");

  const pdfDoc = await PDFDocument.create();
  pdfDoc.setTitle(lesson.title || "Lesson Plan");
  pdfDoc.setSubject("Lesson Plan");

  const fonts = {
    regular: await pdfDoc.embedFont(StandardFonts.Helvetica),
    bold: await pdfDoc.embedFont(StandardFonts.HelveticaBold),
  };
  const w = createWriter({ pdfDoc, fonts, rgb });
  const isCompleted = lesson.status === LESSON_STATUS.COMPLETED;
  const totalDuration = getTotalDuration(lesson.phases);

  /* ---- Title block ---- */
  w.paragraph("LESSON PLAN", { font: fonts.bold, size: SIZE.small, tint: w.color.primary });
  w.space(2);
  w.paragraph(lesson.title, { font: fonts.bold, size: SIZE.title });
  w.space(2);
  w.paragraph(
    [lesson.subjectName, lesson.programName, isCompleted ? "Completed" : "Pending", lesson.isFlagged && "Flagged"]
      .filter(Boolean)
      .join("  ·  "),
    { tint: w.color.muted },
  );
  w.divider();

  w.infoGrid([
    ["Lesson date", formatDate(lesson.lessonDate, "EEEE, d MMM yyyy")],
    ["Time", formatTimeRange(lesson.startTime, lesson.endTime)],
    ["Unit / chapter", lesson.unitChapter],
    ["Teacher", lesson.teacherName],
    ["Creation date", formatDate(lesson.creationDate)],
    ["Duration", totalDuration ? `${totalDuration} min` : ""],
  ]);

  if (lesson.remark) w.callout("Teacher Remarks", lesson.remark);

  /* ---- Learning objectives ---- */
  if (lesson.objectives.length) {
    w.heading("Learning objectives");
    lesson.objectives.forEach((objective, index) => {
      w.paragraph(objective, { prefix: `${index + 1}.` });
      w.space(3);
    });
  }

  /* ---- Lesson flow ---- */
  if (lesson.phases.length) {
    w.heading("Lesson flow", totalDuration ? `${totalDuration} min total` : "");
    lesson.phases.forEach((phase) => {
      const meta = getPhaseMeta(phase.phase);
      w.paragraph(`${meta.label}  (${phase.duration_minutes || 0} min)`, { font: fonts.bold, tint: w.color.primary });
      w.paragraph(phase.content || "—", { indent: 10 });
      w.space(6);
    });
  }

  /* ---- Resources & assessment ---- */
  if (lesson.materials.length) {
    w.heading("Materials & resources");
    lesson.materials.forEach((material) => w.paragraph(material, { prefix: "•" }));
  }
  if (lesson.assessment) {
    w.heading("Assessment / check for understanding");
    w.paragraph(lesson.assessment);
  }
  if (lesson.homework) {
    w.heading("Homework");
    w.paragraph(lesson.homework);
  }

  /* ---- Custom sections ---- */
  lesson.sections.forEach((section) => {
    w.heading(section.name, section.sub_heading);
    if (section.type === SECTION_TYPES.BULLET_LIST) {
      section.items.forEach((item) => w.paragraph(item, { prefix: "•" }));
    } else {
      htmlToLines(section.content).forEach((line) => {
        const isBullet = line.startsWith("• ");
        w.paragraph(isBullet ? line.slice(2) : line, isBullet ? { prefix: "•" } : {});
        w.space(isBullet ? 0 : 4);
      });
    }
  });

  w.footers(formatDate(new Date(), "d MMM yyyy, h:mm aaa"));
  return pdfDoc.save();
};

/** Generates and downloads the lesson plan as a PDF file. */
export const downloadLessonPlanPdf = async (lesson) => {
  const bytes = await buildLessonPlanPdf(lesson);
  const url = window.URL.createObjectURL(new Blob([bytes], { type: "application/pdf" }));
  const link = document.createElement("a");
  link.href = url;
  link.setAttribute("download", toFileName(lesson.title));
  document.body.appendChild(link);
  link.click();
  link.remove();
  setTimeout(() => window.URL.revokeObjectURL(url), 1000);
};
